import { useEffect, useRef, useState } from "react";
import * as api from "./adminApi";
import { AdminShell, Area, Btn, Card, Field, Row, Sel, Text, useAsync } from "./components";

interface SignatoryForm {
  name: string;
  title: string;
  imageRef?: string | null;
}

interface FormState {
  title: string;
  programmeId: string; // "" = global fallback (null)
  bodyHtml: string;
  signatories: SignatoryForm[];
  showScore: boolean;
}

function emptyForm(): FormState {
  return {
    title: "",
    programmeId: "",
    bodyHtml: "",
    signatories: [{ name: "", title: "" }],
    showScore: true,
  };
}

function formFromTemplate(t: api.CertificateTemplateRow): FormState {
  return {
    title: t.title,
    programmeId: t.programmeId ?? "",
    bodyHtml: t.bodyHtml,
    signatories: t.signatories.length > 0 ? t.signatories : [{ name: "", title: "" }],
    showScore: t.showScore,
  };
}

function TemplateForm({
  programmes,
  editing,
  onSaved,
  onSignatureUploaded,
  onCancel,
}: {
  programmes: api.ProgrammeListRow[];
  editing: api.CertificateTemplateRow | null;
  onSaved: () => void;
  onSignatureUploaded: () => void;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<FormState>(editing ? formFromTemplate(editing) : emptyForm());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  async function uploadSignature(index: number, file: File) {
    if (!editing) return;
    setUploadingIndex(index);
    setUploadError(null);
    try {
      await api.uploadSignatoryImage(editing.id, index, file);
      onSignatureUploaded();
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploadingIndex(null);
    }
  }

  useEffect(() => {
    setForm(editing ? formFromTemplate(editing) : emptyForm());
  }, [editing]);

  function setSignatory(i: number, field: keyof SignatoryForm, value: string) {
    setForm((f) => ({
      ...f,
      signatories: f.signatories.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)),
    }));
  }
  function setSignatoryCount(count: number) {
    setForm((f) => {
      const signatories = [...f.signatories];
      while (signatories.length < count) signatories.push({ name: "", title: "" });
      return { ...f, signatories: signatories.slice(0, count) };
    });
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const signatories = form.signatories.filter((s) => s.name.trim() && s.title.trim());
      if (editing) {
        await api.updateCertificateTemplate(editing.id, {
          title: form.title,
          bodyHtml: form.bodyHtml,
          signatories,
          showScore: form.showScore,
        });
      } else {
        await api.createCertificateTemplate({
          title: form.title,
          programmeId: form.programmeId || null,
          bodyHtml: form.bodyHtml,
          signatories,
          showScore: form.showScore,
        });
      }
      onSaved();
      if (!editing) setForm(emptyForm());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const valid =
    form.title.trim().length > 0 &&
    form.bodyHtml.trim().length > 0 &&
    form.signatories.some((s) => s.name.trim() && s.title.trim());

  return (
    <div>
      <Field label="Template title">
        <Text
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        />
      </Field>
      <Field label="Programme (leave blank for a global fallback template)">
        <Sel
          value={form.programmeId}
          onChange={(e) => setForm((f) => ({ ...f, programmeId: String(e.target.value) }))}
          disabled={!!editing}
        >
          <option value="">All programmes (fallback)</option>
          {programmes.map((p) => (
            <option key={p.id} value={p.id}>
              {p.title}
            </option>
          ))}
        </Sel>
        {editing && (
          <p style={{ color: "#888", fontSize: "0.78rem", marginTop: "0.2rem" }}>
            Programme can only be set when a template is created.
          </p>
        )}
      </Field>
      <Field label="Internal note (not printed on the certificate)">
        <Area
          value={form.bodyHtml}
          onChange={(e) => setForm((f) => ({ ...f, bodyHtml: e.target.value }))}
          rows={2}
        />
        <p style={{ color: "#888", fontSize: "0.78rem", marginTop: "0.2rem" }}>
          The branded certificate layout (2026-09-11) is a fixed design with no free-text area —
          this field is just a record of what this template is for, e.g. "Respect@Work® Foundation —
          standard certificate."
        </p>
      </Field>
      <Field label="Number of signatories">
        <Sel
          value={String(form.signatories.length)}
          onChange={(e) => setSignatoryCount(Number(e.target.value))}
        >
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
        </Sel>
      </Field>
      <Field label="Signatories">
        <div>
          {form.signatories.map((s, i) => (
            <div key={i} style={{ marginBottom: "0.6rem" }}>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <Text
                  value={s.name}
                  onChange={(e) => setSignatory(i, "name", e.target.value)}
                  placeholder="Name"
                  style={{ flex: 1 }}
                />
                <Text
                  value={s.title}
                  onChange={(e) => setSignatory(i, "title", e.target.value)}
                  placeholder="Title"
                  style={{ flex: 1 }}
                />
                {editing && (
                  <>
                    <input
                      ref={(el) => {
                        fileInputRefs.current[i] = el;
                      }}
                      type="file"
                      accept=".png,.jpg,.jpeg"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadSignature(i, file);
                        e.target.value = "";
                      }}
                    />
                    <Btn
                      kind="ghost"
                      disabled={uploadingIndex === i}
                      onClick={() => fileInputRefs.current[i]?.click()}
                    >
                      {uploadingIndex === i
                        ? "Uploading…"
                        : s.imageRef
                          ? "Replace signature"
                          : "Upload signature"}
                    </Btn>
                  </>
                )}
              </div>
              {!editing && (
                <p style={{ color: "#888", fontSize: "0.75rem", marginTop: "0.15rem" }}>
                  Save the template first to add a signature image.
                </p>
              )}
            </div>
          ))}
          {uploadError && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{uploadError}</p>}
        </div>
      </Field>
      <label style={{ display: "block", marginBottom: "0.9rem", fontSize: "0.9rem" }}>
        <input
          type="checkbox"
          checked={form.showScore}
          onChange={(e) => setForm((f) => ({ ...f, showScore: e.target.checked }))}
        />{" "}
        Show assessment score on the certificate (when one exists)
      </label>
      {error && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{error}</p>}
      <div style={{ display: "flex", gap: "0.6rem" }}>
        <Btn onClick={save} disabled={busy || !valid}>
          {busy ? "Saving…" : editing ? "Save template" : "Create template"}
        </Btn>
        {onCancel && (
          <Btn kind="ghost" onClick={onCancel}>
            Cancel
          </Btn>
        )}
      </div>
    </div>
  );
}

export function CertificateTemplatesPage() {
  const { data: templates, error, reload } = useAsync(() => api.listCertificateTemplates(), []);
  const { data: programmes } = useAsync(api.listProgrammes, []);
  const { data: failed, reload: reloadFailed } = useAsync(api.listFailedCredentials, []);
  const { data: missing, reload: reloadMissing } = useAsync(api.listMissingCredentials, []);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [reconcilingId, setReconcilingId] = useState<string | null>(null);

  const editing = (templates ?? []).find((t) => t.id === editingId) ?? null;

  return (
    <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Certificate templates">
      {error && <Card>{error}</Card>}

      {failed && failed.length > 0 && (
        <Card>
          <strong>Certificate issues</strong>
          <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "0.2rem" }}>
            These learners passed but certificate generation failed — a passed status is never
            affected by this. Retry once the underlying issue (e.g. no active template) is fixed.
          </p>
          {failed.map((f) => (
            <Row key={f.id}>
              <span>
                {f.enrollment.learner.displayName} · {f.enrollment.track.programme.title} —{" "}
                {f.enrollment.track.title}
                <br />
                <span style={{ color: "#b00020", fontSize: "0.8rem" }}>{f.lastError}</span>
              </span>
              <Btn
                kind="ghost"
                disabled={retryingId === f.enrollment.id}
                onClick={async () => {
                  setRetryingId(f.enrollment.id);
                  try {
                    await api.retryCredential(f.enrollment.id);
                    reloadFailed();
                  } finally {
                    setRetryingId(null);
                  }
                }}
              >
                {retryingId === f.enrollment.id ? "Retrying…" : "Retry"}
              </Btn>
            </Row>
          ))}
        </Card>
      )}

      {missing && missing.length > 0 && (
        <Card>
          <strong>Reconciliation — passed with no certificate record</strong>
          <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "0.2rem" }}>
            These enrolments passed but have no credential record at all (a data anomaly, not the
            ordinary retry case above). "Reconcile" creates the missing record and issues it
            immediately.
          </p>
          {missing.map((m) => (
            <Row key={m.enrollmentId}>
              <span>
                {m.learnerName} · {m.programmeTitle} — {m.trackTitle}
              </span>
              <Btn
                kind="ghost"
                disabled={reconcilingId === m.enrollmentId}
                onClick={async () => {
                  setReconcilingId(m.enrollmentId);
                  try {
                    await api.reconcileCredential(m.enrollmentId);
                    reloadMissing();
                    reloadFailed();
                  } finally {
                    setReconcilingId(null);
                  }
                }}
              >
                {reconcilingId === m.enrollmentId ? "Reconciling…" : "Reconcile"}
              </Btn>
            </Row>
          ))}
        </Card>
      )}

      <Card>
        <strong>Templates</strong>
        {templates?.length === 0 && <p style={{ color: "#666" }}>No certificate templates yet.</p>}
        {templates?.map((t) => (
          <Row key={t.id}>
            <span
              onClick={() => setEditingId(t.id)}
              style={{ cursor: "pointer", color: "#1a1a2e" }}
              title="Click to edit"
            >
              {t.title}
              <span style={{ color: "#888" }}> · {t.programme?.title ?? "All programmes"}</span>
            </span>
            <Btn
              kind="ghost"
              onClick={async () => {
                await api.updateCertificateTemplate(t.id, { active: !t.active });
                reload();
              }}
            >
              {t.active ? "Deactivate" : "Activate"}
            </Btn>
          </Row>
        ))}
      </Card>

      <Card>
        <strong>{editing ? "Edit template" : "New template"}</strong>
        <div style={{ marginTop: "0.75rem" }}>
          <TemplateForm
            programmes={programmes ?? []}
            editing={editing}
            onSaved={() => {
              setEditingId(null);
              reload();
            }}
            onSignatureUploaded={reload}
            onCancel={editing ? () => setEditingId(null) : undefined}
          />
        </div>
      </Card>
    </AdminShell>
  );
}
